import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/config";
import { listTimezones, browserTimezone } from "@/lib/timezones";
import { DATE_ORDER_OPTIONS, detectDateOrder } from "@/lib/csvDates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const OnboardingPage = () => {
  const { user, setOrgId } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [brandColor, setBrandColor] = useState("#303030");
  const [timezone, setTimezone] = useState(browserTimezone());
  const [dateFormat, setDateFormat] = useState(detectDateOrder());
  const timezones = listTimezones();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/onboarding/create-org`,
        { name, contact_email: contactEmail || null, brand_color: brandColor, timezone, date_format: dateFormat },
        { withCredentials: true }
      );
      setOrgId(data.org_id);
      toast.success(`${data.name} is ready to go`);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950" data-testid="onboarding-page">
      <div className="w-full max-w-sm">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white tracking-tight">Welcome</h1>
            <p className="text-sm text-zinc-500 mt-1">Let's set up your club</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-3" data-testid="onboarding-error">
                {error}
              </div>
            )}

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Club / Organization Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 focus:border-[#303030] focus:ring-[#303030]/20"
                placeholder="Example Club"
                required
                data-testid="onboarding-name-input"
              />
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Contact Email</Label>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className="mt-2 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 focus:border-[#303030] focus:ring-[#303030]/20"
                placeholder="club@example.com"
                data-testid="onboarding-contact-email-input"
              />
              <p className="text-xs text-zinc-600 mt-1.5">Used as the reply-to address on pass emails to your members.</p>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Brand Colour</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 focus:border-[#303030] focus:ring-[#303030]/20"
                  placeholder="#303030"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  title="A hex colour like #303030"
                  data-testid="onboarding-brand-color-input"
                />
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="w-12 h-10 rounded-md border border-zinc-700 bg-zinc-800 cursor-pointer"
                  data-testid="onboarding-brand-color-picker"
                />
              </div>
              <p className="text-xs text-zinc-600 mt-1.5">Used for your dashboard's accent colour and the emails your members receive. You can change this later under Account.</p>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Time Zone</Label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="mt-2 w-full h-10 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm px-3 focus:border-[#303030] focus:outline-none"
                data-testid="onboarding-timezone-select"
              >
                {!timezones.includes(timezone) && <option value={timezone}>{timezone.replace(/_/g, " ")}</option>}
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
                ))}
              </select>
              <p className="text-xs text-zinc-600 mt-1.5">Your club's local time — used for membership expiry and the monthly expiring-memberships reminder on the 1st.</p>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date Format</Label>
              <select
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value)}
                className="mt-2 w-full h-10 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm px-3 focus:border-[#303030] focus:outline-none"
                data-testid="onboarding-date-format-select"
              >
                {DATE_ORDER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#303030] hover:bg-[#1A1A1A] text-white font-medium py-2.5"
              disabled={loading}
              data-testid="onboarding-submit-btn"
            >
              {loading ? "Creating..." : "Create Club"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
